<template>
  <div class="investment-input">
    <SingleInput
      v-model="moneyValueFormatted"
      type="number"
      placeholder="0,00"
      prefix="R$"
      show-clear-button
      :status="status"
      :helper-text="helperText"
      @change="handleChange"
      @on-clear="onClear"
      @blur="$emit('blur')"
    >
      <template v-if="showButtons" #right-content>
        <div class="investment-input__button-container">
          <ButtonIconContextual
            class="investment-input__button"
            label="Diminuir"
            icon="EF0020"
            :disabled="loading"
            @click="decrementClick"
          />
          <ButtonIconContextual
            class="investment-input__button"
            label="Aumentar"
            icon="EF0010"
            :disabled="loading"
            @click="incrementClick"
          />
        </div>
      </template>
    </SingleInput>
    <ShortcutValue
      v-if="shortcutValue.length > 0"
      class="investment-input__shortcut"
      :values="shortcutValue"
      :disabled="loading"
      @click="shortcutClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { VMoney } from 'v-money';
import { Decimal } from 'decimal.js';

import {
  SingleInput,
  ShortcutValue,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';

import { currencyToNumber } from '@/modules/common/helpers/currency';
import { IShortcutValue, EShortcutType } from './types';
import { IncrementOperation } from '../../services/product-investment';

const options = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
const numberFormat = new Intl.NumberFormat('pt-BR', options);

interface Props {
  shortcutValue: IShortcutValue[];
  status?: string;
  value?: string;
  originalValue?: number;
  helperText?: string;
  minimum?: number;
  useCalculatorRoute?: boolean;
  increment?: number;
  showButtons?: boolean;
  updateValue?: boolean;
  loading?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  status: '',
  helperText: '',
  value: '',
  originalValue: 0,
  minimum: 0,
  increment: 0,
  useCalculatorRoute: false,
  showButtons: false,
  updateValue: false,
  loading: false
});

interface EmitEvents {
  (
    event: 'change',
    data: {
      value: number;
      formatted: string;
      increment: boolean;
      operation?: IncrementOperation;
    }
  ): void;
  (event: 'blur'): void;
}
const emit = defineEmits<EmitEvents>();

const moneyValueFormatted = ref('');
const moneyValue = ref(props.originalValue);

function handleChange(event: Event) {
  const target = event.target as HTMLInputElement;

  moneyValueFormatted.value = target.value.replace('-', '');
  moneyValue.value = currencyToNumber(moneyValueFormatted.value);

  emitChange();
}

function onClear() {
  moneyValueFormatted.value = '0,00';
  moneyValue.value = 0;

  emitChange();
}

function shortcutClick(data: IShortcutValue) {
  const shortCutValue = new Decimal(data.value);
  const currentValue = new Decimal(moneyValue.value);

  if (data.type === EShortcutType.SET) {
    moneyValueFormatted.value = numberFormat.format(shortCutValue.toNumber());
    moneyValue.value = shortCutValue.toNumber();
  } else {
    moneyValueFormatted.value = numberFormat.format(
      currentValue.plus(shortCutValue).toNumber()
    );
    moneyValue.value = currentValue.plus(shortCutValue).toNumber();
  }

  emitChange(true);
}

function incrementClick() {
  const incrementTrunked = Math.trunc(props.increment * 100) / 100;
  const currentValueToCompare = moneyValue.value || incrementTrunked;

  if (props.useCalculatorRoute && currentValueToCompare >= props.minimum) {
    emitChange(true, IncrementOperation.Add);
    return;
  }

  const currentValue = new Decimal(moneyValue.value);
  const increment = new Decimal(props.increment);

  if (currentValue.toNumber() < props.minimum) {
    moneyValueFormatted.value = numberFormat.format(props.minimum);
    moneyValue.value = props.minimum;
    emitChange(true);
    return;
  }

  const finalValue = currentValue.plus(increment);

  moneyValueFormatted.value = numberFormat.format(
    finalValue.times(100).floor().dividedBy(100).toNumber()
  );
  moneyValue.value = finalValue.toNumber();

  emitChange(true);
}

function decrementClick() {
  const currentValue = new Decimal(moneyValue.value);
  const incrementTrunked = Math.trunc(currentValue.toNumber() * 100) / 100;

  if (incrementTrunked <= props.minimum) {
    moneyValueFormatted.value = numberFormat.format(0);
    moneyValue.value = 0;

    emitChange(false);
    return;
  }

  if (props.useCalculatorRoute) {
    emitChange(true, IncrementOperation.Subtract);
    return;
  }

  const decrement = new Decimal(props.increment);
  const calcValue = currentValue.minus(decrement);
  const finalValue = calcValue.toNumber() < 1 ? new Decimal(0) : calcValue;

  moneyValueFormatted.value = numberFormat.format(
    finalValue.times(100).floor().dividedBy(100).toNumber()
  );
  moneyValue.value = finalValue.toNumber();

  emitChange(true);
}

function emitChange(increment = false, operation?: IncrementOperation) {
  const data = {
    value: moneyValue.value,
    formatted: moneyValueFormatted.value,
    increment,
    operation
  };

  emit('change', data);
}

watch(
  () => props.updateValue,
  updateValue => {
    if (updateValue) {
      moneyValueFormatted.value = props.value.replace('R$', '').trim();
      moneyValue.value = props.originalValue;
    }
  },
  { immediate: true }
);

onMounted(() => {
  const textarea = document.querySelector('textarea') as HTMLElement;
  VMoney(textarea, {
    value: {
      decimal: ',',
      thousands: '.',
      prefix: '',
      precision: 2
    }
  });
});
</script>

<style lang="less" scoped>
.investment-input {
  &__shortcut {
    margin-top: @size-spacing-x400;
  }

  &__button-container {
    display: flex;
  }

  &__button {
    background-color: @background-tertiary;
    margin-left: @size-spacing-x300;
  }
}
</style>
